import React, { useEffect } from 'react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { validateEMA } from './services/indicators/validateEMA';
import ReactDOM from 'react-dom/client';
import './index.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { initViewportHeight } from './utils/viewportHeight';
import { isPrerendering } from './utils/prerendering';
import { normalizeHtmlExtensionPath } from './utils/normalizeHtmlExtensionPath';

// /landing.html → /landing so Router + hydration match react-snap output (build/landing/index.html)
normalizeHtmlExtensionPath();

// Benign browser quirk when chart/layout resize runs inside ResizeObserver — suppress CRA dev overlay.
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (
      event.message === 'ResizeObserver loop completed with undelivered notifications.' ||
      event.message === 'ResizeObserver loop limit exceeded'
    ) {
      event.stopImmediatePropagation();
    }
  });
}

// Check if we're being pre-rendered by react-snap
const isPrerender = isPrerendering();

// Override console methods to filter out PostHog logs before PostHog initializes
if (typeof window !== 'undefined' && window.console) {
  const originalLog = console.log;
  const originalInfo = console.info;

  console.log = (...args) => {
    const message = args[0];
    if (typeof message === 'string' && message.includes('[PostHog.js]')) return;
    originalLog.apply(console, args);
  };

  console.info = (...args) => {
    const message = args[0];
    if (typeof message === 'string' && message.includes('[PostHog.js]')) return;
    originalInfo.apply(console, args);
  };
}

const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');
// Allow opt-in PostHog tracking on localhost for testing
const enablePosthogLocal = process.env.REACT_APP_ENABLE_POSTHOG_LOCAL === 'true';
// Disable PostHog during pre-rendering to avoid network errors
const enablePosthog = !isPrerender && (!isLocalHost || enablePosthogLocal);
const sentryEnvironment = process.env.REACT_APP_SENTRY_ENV || (isLocalHost ? 'local' : process.env.NODE_ENV);
const sentryDsn = process.env.REACT_APP_SENTRY_DSN || 'https://7e283d149053956eac31a3b0229dce23@o4509895088078848.ingest.us.sentry.io/4509895141294080';

// Initialize viewport height tracking for mobile devices
// This fixes the 100vh issue where mobile browser UI is included in the viewport
initViewportHeight();

const rootElement = document.getElementById('root');

// Disable PostHog tracking in local development to keep analytics clean
const posthogOptions = {
  api_host: process.env.REACT_APP_POSTHOG_HOST,
  defaults: '2025-05-24',
  capture_exceptions: enablePosthog, // Enable exceptions when PostHog enabled
  debug: false, // Disable console logging
  // Disable all tracking when running locally unless explicitly enabled
  autocapture: enablePosthog,
  // We capture pageviews manually (so we can attach landing variant)
  capture_pageview: false,
  capture_pageleave: enablePosthog,
  disable_session_recording: isLocalHost && !enablePosthogLocal,
};

// Only provide API key when PostHog is enabled
const posthogApiKey = enablePosthog ? process.env.REACT_APP_POSTHOG_KEY : '';

function scheduleAfterFirstPaint(callback) {
  const scheduleIdle = (fn) => {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 2000 });
    } else {
      setTimeout(fn, 0);
    }
  };
  requestAnimationFrame(() => {
    setTimeout(() => scheduleIdle(callback), 0);
  });
}

function initSentryDeferred(SentryModule) {
  SentryModule.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    sendDefaultPii: true,
    // Drop network fetch errors only when they originate from non-critical, fire-and-forget
    // sources (analytics, PostHog). Errors from the same TypeError class that come from
    // trading or auth fetches will still surface normally.
    beforeSend(event, hint) {
      const error = hint?.originalException;
      const message = error?.message || event?.exception?.values?.[0]?.value || '';
      if (/ResizeObserver loop/i.test(message)) {
        return null;
      }
      if (error instanceof TypeError && /load failed|failed to fetch/i.test(error.message)) {
        const frames = event?.exception?.values?.[0]?.stacktrace?.frames ?? [];
        const isNonCritical = frames.some(f =>
          f.filename?.includes('analyticsService') ||
          f.filename?.includes('posthog')
        );
        if (isNonCritical) return null;
      }
      return event;
    },
  });
}

function DeferredTelemetryRoot() {
  useEffect(() => {
    if (isPrerender) return;
    let cancelled = false;

    scheduleAfterFirstPaint(async () => {
      try {
        const sentryModule = await import('@sentry/react');
        if (!cancelled) {
          initSentryDeferred(sentryModule);
        }
      } catch (error) {
        console.error('Deferred Sentry init failed:', error);
      }

      if (!posthogApiKey) return;
      try {
        // Init the posthog-js singleton without wrapping <App /> in PostHogProvider.
        // Swapping the tree to wrap App after first paint remounted the whole app and
        // re-fired every analytics useEffect (page_visit, onboarding, identify, …).
        const { default: posthog } = await import('posthog-js');
        if (cancelled) return;
        if (typeof posthog.init === 'function' && !posthog.__loaded) {
          posthog.init(posthogApiKey, posthogOptions);
        }
      } catch (error) {
        console.error('Deferred PostHog init failed:', error);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <App />
      {/* Skip SpeedInsights during pre-rendering - it requires Vercel's server */}
      {!isPrerender && <SpeedInsights />}
    </>
  );
}

// Use hydrate when react-snap pre-rendered the page (for crawlers/AI accessibility)
if (rootElement.hasChildNodes()) {
  ReactDOM.hydrateRoot(rootElement, <DeferredTelemetryRoot />);
} else {
  ReactDOM.createRoot(rootElement).render(<DeferredTelemetryRoot />);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Register service worker for push notifications (after React has rendered)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then(registration => {
        console.log('Service Worker registered with scope:', registration.scope);
      })
      .catch(error => {
        console.error('Service Worker registration failed:', error);
      });
  });
}

// Expose validation tools to console for testing
if (process.env.NODE_ENV === 'development') {
  window.validateEMA = validateEMA;
  console.log('🔧 Dev tools available: window.validateEMA(ticker, period, numBars)');
}